import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

// prop-types est une biblioth�que pour la v�rification des types des props
import PropTypes from "prop-types";

// composants react-table
import { useTable } from "react-table";

// composants de @mui
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";

import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import TextField from "@mui/material/TextField";

// Material Dashboard 2 React composants
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// composants d'exemple de Material Dashboard 2 React
import DataTableHeadCell from "examples/Tables/DataTableV2/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTableV2/DataTableBodyCell";

// Import du contexte API
import { useApiContext } from 'context/ApiContext';

function DataTable({
    table,
    rowsNumberPerPage = 10,
    isSorted,
    selectedRowIndex,
    setSelectedRowIndex,
}) {
    const columns = useMemo(() => table.columns, [table]);
    const data = useMemo(() => table.rows, [table]);

    const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } = useTable({ columns, data });
    const { APIRequestType, getAPI, setSelectedAlertIDTuD, setCameraOrientationTuD } = useApiContext();
    const navigate = useNavigate();
    const { pathname } = window.location;

    const [currentPage, setCurrentPage] = useState(0);
    //const rowsPerPage = 10;
    const rowsPerPage = rowsNumberPerPage;
    const totalPages = Math.ceil(rows.length / rowsPerPage);

    const pageRows = rows.slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage);

    const handleRowClick = (rowIndex, rowOriginal, event) => {
        event.preventDefault();
        const globalIndex = currentPage * rowsPerPage + rowIndex;

        if (pathname === "/parkList") {
            localStorage.setItem("selectedParkID", rowOriginal.parkidValue);
            navigate("/parkDashboard", { replace: true });
        } else if (pathname === "/parkDashboard") {
            localStorage.setItem("selectedTurbineID", rowOriginal.turbineidValue);
            navigate("/turbineDashboard", { replace: true });
        } else if (pathname === "/turbineDashboard") {
            if (selectedRowIndex !== globalIndex) {
                setSelectedRowIndex(globalIndex);
                setCameraOrientationTuD(rowOriginal.cameraValue);
                setSelectedAlertIDTuD(rowOriginal.alertidValue);
                getAPI(APIRequestType.GET_IMAGE, rowOriginal.alertidValue);
                getAPI(APIRequestType.GET_VIDEO, rowOriginal.alertidValue);
            }
        }
    };

    const setSortedValue = (column) => {
        if (isSorted && column.isSorted) {
            return column.isSortedDesc ? "desc" : "asce";
        }
        return isSorted ? "none" : false;
    };

    const goToPage = (page) => {
        setCurrentPage(page);
    };

    const renderPagination = () => {
        if (totalPages <= 5) {
            return [...Array(totalPages)].map((_, i) => (
                <Typography
                    key={i}
                    onClick={() => goToPage(i)}
                    sx={{
                        mx: 1,
                        cursor: "pointer",
                        fontWeight: i === currentPage ? "bold" : "normal",
                        color: i === currentPage ? "#1976d2" : "inherit",
                        fontSize: "1rem"
                    }}
                >
                    {i + 1}
                </Typography>
            ));
        }

        const pages = [];
        pages.push(0);

        if (currentPage > 2) {
            pages.push("...");
        }

        for (let i = Math.max(1, currentPage - 1); i <= Math.min(totalPages - 2, currentPage + 1); i++) {
            pages.push(i);
        }

        if (currentPage < totalPages - 3) {
            pages.push("...");
        }

        pages.push(totalPages - 1);

        return pages.map((page, index) => (
            <Typography
                key={index}
                onClick={() => typeof page === "number" && goToPage(page)}
                sx={{
                    mx: 1,
                    cursor: page === "..." ? "default" : "pointer",
                    fontWeight: page === currentPage ? "bold" : "normal",
                    color: page === currentPage ? "#1976d2" : "inherit",
                    fontSize: "0.8rem"
                }}
            >
                {page === "..." ? "..." : page + 1}
            </Typography>
        ));
    };

    const [inputPage, setInputPage] = useState(currentPage + 1); // Etat pour gerer l'input de la page

    const handleInputChange = (event) => {
        const value = parseInt(event.target.value, 10);
        if (!isNaN(value) && value > 0 && value <= totalPages) {
            setInputPage(value);
        }
    };

    const handleGoToPage = () => {
        const page = inputPage - 1; // Le numero de page est decale de 1
        goToPage(page);
    };

    // Gestion de l'evenement "Entree" pour appliquer la page
    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            handleGoToPage(); // Applique la page quand "Entree" est pressee
        }
    };

    return (
        <MDBox>
            <TableContainer>
                <Table {...getTableProps()}>
                    <thead>
                        {headerGroups.map((headerGroup) => (
                            <TableRow {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                                {headerGroup.headers.map((column) => (
                                    <DataTableHeadCell
                                        key={column.id}
                                        {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                                        width={column.width ? column.width : "auto"}
                                        align={column.align ? column.align : "left"}
                                        sorted={setSortedValue(column)}
                                    >
                                        {column.render("Header")}
                                    </DataTableHeadCell>
                                ))}
                            </TableRow>
                        ))}
                    </thead>
                    <TableBody {...getTableBodyProps()}>
                        {pageRows.map((row, pageIndex) => {
                            prepareRow(row);
                            const globalIndex = currentPage * rowsPerPage + pageIndex;

                            return (
                                <TableRow
                                    {...row.getRowProps()}
                                    key={row.id}
                                    onClick={(event) => handleRowClick(pageIndex, row.original, event)}
                                    sx={{
                                        cursor: "pointer",
                                        backgroundColor: selectedRowIndex === globalIndex ? "#b7e4fd" : "transparent"
                                    }}
                                >
                                    {row.cells.map((cell) => (
                                        <DataTableBodyCell
                                            key={cell.column.id}
                                            noBorder={false}
                                            align={cell.column.align || "left"}
                                            isSelected={selectedRowIndex === globalIndex}
                                            textColor={row.original.isInactive ? "secondary" : "#344767"}
                                        >
                                            {cell.render("Cell")}
                                        </DataTableBodyCell>
                                    ))}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {totalPages > 1 && (
                <MDBox sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 2 }}>
                    <IconButton onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 0}>
                        <ArrowBackIcon />
                    </IconButton>

                    {renderPagination()}

                    <IconButton onClick={() => goToPage(currentPage + 1)} disabled={currentPage >= totalPages - 1}>
                        <ArrowForwardIcon />
                    </IconButton>
                </MDBox>
            )}

            {/* Ajout de la zone "Go to" centree sous la pagination */}
            {totalPages > 1 && (
                <MDBox sx={{ display: "flex", justifyContent: "center", alignItems: "center", mb: 1 }}>
                    <MDTypography variant="button"
                        fontWeight="light"
                        color="dark"
                        ml={1}
                        fontSize="0.8rem"
                        sx={{ mr: 1 }}>
                        Go to:
                    </MDTypography>
                    <TextField
                        type="number"
                        value={inputPage}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}  // Detecte l'appui sur "Entree"
                        sx={{ width: 50, mr: 1.5 }}
                        inputProps={{
                            min: 1,
                            max: totalPages,
                            style: { fontSize: '0.8rem' }
                        }}
                    />
                    <MDTypography variant="button"
                        fontWeight="light"
                        color="dark"
                        ml={1}
                        fontSize="0.8rem"
                        sx={{ mr: 1, ml: -0.5 }}>
                        of {totalPages}
                    </MDTypography>
                </MDBox>
            )}
        </MDBox>
    );
}

DataTable.propTypes = {
    table: PropTypes.object.isRequired,
};

export default DataTable;
